import type {
  RecordsQuery,
  RedemptionRecord,
  SubscriptionRecord,
  Vault,
} from 'src/pages/admin/services/vaultService';
import type { ApiErrorInfo } from 'src/pages/admin/utils/decimal-input';

import { ENGINE_ERROR_KEYS } from '../params/helpers';
import type { StateColor } from '../vaults/helpers';
import { formatInstant, formatNav, formatPercent, formatUnits, formatUsdc } from '../vaults/helpers';
import {
  ALL_VAULTS,
  CLOSURE_STATUSES,
  RECORDS_NS as NS,
  RECORD_TABS,
  REJECTION_BLOCKERS,
  RecordTab,
  SUBSCRIPTION_RECORD_STATUSES,
  VAULT_FILTER_INVALID,
} from './constants';

const EMPTY = '—';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * The filters as the URL carries them (PRD §14.1: state lives in the URL). `vault` is a vault id, `all`, or null
 * when the URL names none; the others are '' when unset. `from` / `to` are UTC instants (ISO 8601).
 */
export interface RecordsFilter {
  vault: string | null;
  holderId: string;
  status: string;
  from: string;
  to: string;
}

export const EMPTY_FILTER: RecordsFilter = { vault: null, holderId: '', status: '', from: '', to: '' };

/** `?vault=…&holder=…&status=…&from=…&to=…` → the filter; unknown keys are ignored. */
export const parseRecordsSearch = (search: string): RecordsFilter => {
  const params = new URLSearchParams(search);
  return {
    vault: params.get('vault'),
    holderId: params.get('holder') ?? '',
    status: params.get('status') ?? '',
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
  };
};

const searchOf = (filter: RecordsFilter): string => {
  const params = new URLSearchParams();
  if (filter.vault) params.set('vault', filter.vault);
  if (filter.holderId.trim()) params.set('holder', filter.holderId.trim());
  if (filter.status) params.set('status', filter.status);
  // The BFF refuses a half range, so a half range is not written either.
  if (filter.from && filter.to) {
    params.set('from', filter.from);
    params.set('to', filter.to);
  }
  const s = params.toString();
  return s ? `?${s}` : '';
};

/** The page's own URL for a tab and its filters: what the tabs, the switcher and Apply navigate to. */
export const recordsPath = (root: string, tab: RecordTab, filter: RecordsFilter): string =>
  `${root}/${tab}${searchOf(filter)}`;

/** Changes with the tab or any filter, so a page index chosen under one never carries over to another. */
export const filterKey = (tab: RecordTab, filter: RecordsFilter): string =>
  `${tab}${searchOf(filter)}`;

export const isRecordTab = (value: string | undefined): value is RecordTab =>
  (RECORD_TABS as readonly string[]).includes(value ?? '');

/** The statuses a tab's filter can take: each tab has its own enum. */
export const statusesOf = (tab: RecordTab): readonly string[] =>
  tab === 'subscriptions' ? SUBSCRIPTION_RECORD_STATUSES : CLOSURE_STATUSES;

/**
 * The filter as this tab reads it: a status outside the tab's enum — the other tab's, or a hand-typed URL — is unset,
 * so the BFF never sees a status it would refuse and the status menu never holds a value it does not list.
 */
export const forTab = (filter: RecordsFilter, tab: RecordTab): RecordsFilter =>
  statusesOf(tab).includes(filter.status) ? filter : { ...filter, status: '' };

/**
 * Which vault the tabs show. No vault in the URL opens the first vault and writes it there — the common read then
 * stays on the vault's own index (vault_admin_records.go) — and an id the console does not list falls back the same
 * way (§14.1). `all` is an explicit choice: every vault, no redirect. `vaultId` is null for `all` and while there is
 * nothing to show.
 */
export const resolveRecordsVault = (
  vaults: Vault[],
  raw: string | null
): { vaultId: string | null; redirect: string | null; unknown: boolean; all: boolean } => {
  if (raw === ALL_VAULTS) return { vaultId: null, redirect: null, unknown: false, all: true };
  if (raw && vaults.some((v) => v.vaultId === raw))
    return { vaultId: raw, redirect: null, unknown: false, all: false };
  const first = vaults[0]?.vaultId ?? null;
  return { vaultId: null, redirect: first, unknown: !!raw, all: false };
};

/** The BFF query: only the filters that are set, the range as a pair, and the page. */
export const recordsQuery = (
  filter: RecordsFilter,
  vaultId: string | null,
  offset: number,
  limit: number
): RecordsQuery => {
  const query: RecordsQuery = { offset, limit };
  if (vaultId) query.vaultId = vaultId;
  const holder = filter.holderId.trim();
  if (holder) query.holderId = holder;
  if (filter.status) query.status = filter.status;
  if (filter.from && filter.to) {
    query.from = filter.from;
    query.to = filter.to;
  }
  return query;
};

/**
 * Two `datetime-local` values (browser zone) as UTC instants, both inclusive. null while either end is empty (no
 * range); `error` when an end is not a date or the range ends before it starts — refused here, not sent.
 */
export const toIsoRange = (
  localFrom: string,
  localTo: string
): { from?: string; to?: string; error?: 'invalid' | 'reversed' } | null => {
  if (!localFrom || !localTo) return null;
  const from = new Date(localFrom).getTime();
  const to = new Date(localTo).getTime();
  if (Number.isNaN(from) || Number.isNaN(to)) return { error: 'invalid' };
  if (from > to) return { error: 'reversed' };
  return { from: new Date(from).toISOString(), to: new Date(to).toISOString() };
};

/** A recorded instant as a `datetime-local` value in the browser's zone; '' when absent or unreadable. */
export const toLocalInput = (iso: string): string => {
  const ms = iso ? Date.parse(iso) : NaN;
  if (Number.isNaN(ms)) return '';
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * PRD §14.6 status words. SUBSCRIBED reads "Filled"; a rejection reads "Rejected · <blocker>" with the blocker the
 * runner recorded (the PRD names two; the rest read with neutral words). A blocker added later shows raw.
 */
export const subscriptionStatus = (
  r: SubscriptionRecord,
  translate: Translate
): { label: string; color: StateColor } => {
  if (r.status === 'SUBSCRIBED') return { label: translate(`${NS}.status.SUBSCRIBED`), color: 'success' };
  const code = r.rejection?.blocker;
  const known = !!code && (REJECTION_BLOCKERS as readonly string[]).includes(code);
  const blocker = known ? translate(`${NS}.blocker.${code}`) : (code ?? EMPTY);
  return { label: translate(`${NS}.status.REJECTED`, { blocker }), color: 'error' };
};

const CLOSURE_COLOR: Record<string, StateColor> = {
  ISSUED: 'info',
  CLOSING: 'info',
  EXECUTED: 'success',
  ROLLED_BACK: 'error',
  ESCALATED: 'warning',
};

/** Paid = EXECUTED, Closing = ISSUED | CLOSING, Rolled back; ESCALATED is its own chip. A state added later shows raw. */
export const redemptionStatus = (
  status: string,
  translate: Translate
): { label: string; color: StateColor } => {
  if (!(CLOSURE_STATUSES as readonly string[]).includes(status)) return { label: status, color: 'default' };
  return { label: translate(`${NS}.closure.${status}`), color: CLOSURE_COLOR[status] };
};

export interface SubscriptionCells {
  amount: string;
  units: string;
  fillNav: string;
  arrivedAt: string;
  executedAt: string;
  refund: { basis: string; refunded: boolean } | null;
}

/**
 * One Subscriptions row as text: USDC at 2 dp, units at their 6 dp, the fill NAV at 4 dp, local-time instants. A rejection
 * has no fill, so units and NAV are "—" (never 0) and its instant is the rejection's.
 */
export const subscriptionCells = (r: SubscriptionRecord): SubscriptionCells => ({
  amount: formatUsdc(r.amount),
  units: r.sharesIssued === null ? EMPTY : formatUnits(r.sharesIssued),
  fillNav: r.navExecuted === null ? EMPTY : formatNav(r.navExecuted),
  arrivedAt: formatInstant(r.arrivedAt),
  executedAt: r.executedAt
    ? formatInstant(r.executedAt)
    : r.rejection
      ? formatInstant(r.rejection.rejectedAt)
      : EMPTY,
  refund: r.rejection ? { basis: formatUsdc(r.rejection.refundBasis), refunded: r.rejection.refunded } : null,
});

export interface RedemptionCells {
  orderId: string;
  requested: string;
  x: string;
  recovered: string;
  slippage: string;
  fee: string;
  feeForgone: string;
  net: string;
  issuedAt: string;
  executedAt: string;
  reason: string | null;
}

/**
 * One Redemptions row as text. The order id is the holder's own clientOrderId, else the instruction id. The settled
 * figures exist only at EXECUTED and are "—" otherwise, never 0. x is the close ratio as a percentage.
 */
export const redemptionCells = (r: RedemptionRecord): RedemptionCells => {
  const usdc = (v: string | null) => (v == null ? EMPTY : formatUsdc(v));
  return {
    orderId: r.clientOrderId ?? r.id,
    requested: formatUsdc(r.requested),
    x: formatPercent(r.fractionX),
    recovered: usdc(r.recovered),
    slippage: usdc(r.slippage),
    fee: usdc(r.fee),
    feeForgone: usdc(r.feeForgone),
    net: usdc(r.net),
    issuedAt: formatInstant(r.issuedAt),
    executedAt: r.executedAt ? formatInstant(r.executedAt) : EMPTY,
    reason: r.escalationReason ?? r.failureReason ?? null,
  };
};

/** The load banner: one translated line; the raw upstream text is not shown. */
export const recordsErrorMessage = (info: ApiErrorInfo, translate: Translate): string => {
  if (info.code === VAULT_FILTER_INVALID) return translate(`${NS}.filterInvalid`);
  return translate((info.code && ENGINE_ERROR_KEYS[info.code]) || `${NS}.loadFailed`);
};
